import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { ChevronLeft, ChevronRight, Sparkles, Calendar as CalIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function FareCalendar({ slug }) {
  const { format } = useCurrency();
  const nav = useNavigate();
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() + 1 };
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const monthStr = `${cursor.y}-${String(cursor.m).padStart(2, "0")}`;

  useEffect(() => {
    setLoading(true);
    api.get(`/packages/${slug}/fares`, { params: { month: monthStr } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [slug, monthStr]);

  const gridStart = useMemo(() => {
    if (!data?.fares?.length) return 0;
    // Monday-first — weekday 0=Mon in Python; JS: getDay 0=Sun. Our API sends weekday Mon=0.
    return data.fares[0].weekday;
  }, [data]);

  const goPrev = () => {
    setCursor((c) => {
      const m = c.m - 1;
      return m < 1 ? { y: c.y - 1, m: 12 } : { y: c.y, m };
    });
  };
  const goNext = () => {
    setCursor((c) => {
      const m = c.m + 1;
      return m > 12 ? { y: c.y + 1, m: 1 } : { y: c.y, m };
    });
  };

  const today = new Date();
  const canGoPrev = !(cursor.y === today.getFullYear() && cursor.m === today.getMonth() + 1);

  const cheapest = data?.fares?.find((f) => f.is_cheapest);

  return (
    <div data-testid="fare-calendar" className="rounded-3xl p-6 sm:p-8 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#D4AF37] font-semibold">
            <CalIcon className="w-3.5 h-3.5" /> Fare Calendar
          </div>
          <h3 className="mt-1 text-2xl font-bold text-blue-950 dark:text-white">
            {MONTH_NAMES[cursor.m - 1]} {cursor.y}
          </h3>
          {cheapest && (
            <div className="mt-1 text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Cheapest date: {new Date(cheapest.date).toDateString().slice(0, 10)} at {format(cheapest.price)}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="cal-prev"
            onClick={goPrev}
            disabled={!canGoPrev}
            className="w-10 h-10 rounded-full border border-slate-200 dark:border-white/10 flex items-center justify-center disabled:opacity-30"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            data-testid="cal-next"
            onClick={goNext}
            className="w-10 h-10 rounded-full border border-slate-200 dark:border-white/10 flex items-center justify-center"
            aria-label="Next month"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5 mb-2">
        {DAY_LABELS.map((d) => (
          <div key={d} className="text-[10px] uppercase tracking-widest text-slate-400 text-center py-1">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {Array.from({ length: gridStart }).map((_, i) => (
          <div key={`sp-${i}`} />
        ))}
        {loading
          ? Array.from({ length: 30 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
            ))
          : data?.fares?.map((f) => {
              const isSelected = selected === f.date;
              const disabled = !f.available;
              return (
                <button
                  key={f.date}
                  data-testid={`cal-day-${f.day}`}
                  onClick={() => !disabled && setSelected(f.date)}
                  disabled={disabled}
                  className={`aspect-square min-h-[64px] rounded-xl p-1.5 flex flex-col items-center justify-center text-center border transition-all ${
                    disabled
                      ? "opacity-30 cursor-not-allowed border-transparent"
                      : isSelected
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 shadow-lg"
                      : f.is_cheapest
                      ? "border-emerald-400/60 bg-emerald-50 dark:bg-emerald-500/10 hover:border-emerald-500"
                      : "border-slate-200 dark:border-white/10 hover:border-[#D4AF37] bg-slate-50 dark:bg-slate-800/40"
                  }`}
                >
                  <div className={`text-sm font-bold ${
                    isSelected ? "text-[#D4AF37]"
                    : f.is_cheapest ? "text-emerald-700 dark:text-emerald-300"
                    : "text-blue-950 dark:text-white"
                  }`}>
                    {f.day}
                  </div>
                  <div className="text-[9px] font-semibold text-slate-500 dark:text-slate-400 leading-tight">
                    {format(f.price)}
                  </div>
                  {f.is_cheapest && <div className="text-[8px] text-emerald-600 dark:text-emerald-400 leading-tight">✦ Best</div>}
                </button>
              );
            })}
      </div>

      {selected && (
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3 rounded-2xl p-4 bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/30">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Selected date</div>
            <div className="text-lg font-bold text-blue-950 dark:text-white">
              {new Date(selected).toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            </div>
            <div className="text-sm text-[#D4AF37] font-semibold mt-1">
              {format(data.fares.find((x) => x.date === selected).price)} / person
            </div>
          </div>
          <button
            data-testid="cal-book-selected"
            onClick={() => nav(`/book/${slug}?date=${selected}`)}
            className="px-6 py-3 rounded-full btn-gold text-sm"
          >
            Continue with this date →
          </button>
        </div>
      )}

      <div className="mt-4 flex items-center gap-4 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-emerald-500/40" /> Best deal</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-[#D4AF37]" /> Selected</span>
        <span>· Prices dynamic. Weekends & festival months priced higher.</span>
      </div>
    </div>
  );
}
